import type { components } from '@/api/schema';

import { keysOf, labelOf, optionsOf } from './humanize';

type Schemas = components['schemas'];

/** The fourteen states the server enforces. */
export type OpportunityStatus = Schemas['OpportunityTransition']['status'];
export type DecisionMakerStatus = Schemas['ConsultationView']['decisionMaker'];

/** Why an opportunity is asking for a person; the schema carries these as plain strings. */
export type AttentionReason =
  | 'LEAD_REMINDER'
  | 'SAFETY_ESCALATION'
  | 'MISSING_STATUS'
  | 'MISSING_NEXT_ACTION'
  | 'OVERDUE_ACTION'
  | 'STALE_CONTACT';

/** The seven stages a person sees; the exact state is shown beside it. */
export type PipelinePhase =
  'LEAD' | 'CONSULT' | 'PROPOSE' | 'DECIDE' | 'INSTALL' | 'AFTERCARE' | 'CLOSED';

export const PIPELINE_STATE_LABELS: Record<OpportunityStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  APPOINTMENT_SCHEDULED: 'Sales visit scheduled',
  CONSULTATION_COMPLETE: 'Consultation complete',
  PROPOSAL_PENDING: 'Proposal presented',
  HUMAN_FOLLOW_UP: 'Follow-up needed',
  SOLD: 'Sold',
  LOST: 'Lost',
  NO_DECISION: 'No decision',
  INSTALLATION_SCHEDULED: 'Installation scheduled',
  INSTALLED: 'Installed',
  POST_INSTALL_FOLLOW_UP: 'Post-install follow-up',
  CLOSED: 'Closed',
};

export const PIPELINE_PHASE_LABELS: Record<PipelinePhase, string> = {
  LEAD: 'Lead',
  CONSULT: 'Consult',
  PROPOSE: 'Propose',
  DECIDE: 'Decide',
  INSTALL: 'Install',
  AFTERCARE: 'Aftercare',
  CLOSED: 'Closed',
};

// SOLD opens Install, not Decide: the decision is already made and what is left is the handoff.
// A proposal waiting on the customer and a customer who answered but is not yet confirmed by a
// person are both still Decide. Lost, no-decision and closed are all the end of the road.
const PHASE_OF_STATE: Record<OpportunityStatus, PipelinePhase> = {
  NEW: 'LEAD',
  CONTACTED: 'LEAD',
  QUALIFIED: 'LEAD',
  APPOINTMENT_SCHEDULED: 'CONSULT',
  CONSULTATION_COMPLETE: 'PROPOSE',
  PROPOSAL_PENDING: 'DECIDE',
  HUMAN_FOLLOW_UP: 'DECIDE',
  SOLD: 'INSTALL',
  INSTALLATION_SCHEDULED: 'INSTALL',
  INSTALLED: 'AFTERCARE',
  POST_INSTALL_FOLLOW_UP: 'AFTERCARE',
  LOST: 'CLOSED',
  NO_DECISION: 'CLOSED',
  CLOSED: 'CLOSED',
};

export const ATTENTION_REASON_LABELS: Record<AttentionReason, string> = {
  LEAD_REMINDER: 'Lead reminder due',
  SAFETY_ESCALATION: 'Safety concern raised',
  MISSING_STATUS: 'No status set',
  MISSING_NEXT_ACTION: 'No next action set',
  OVERDUE_ACTION: 'Next action overdue',
  STALE_CONTACT: 'No recent contact',
};

export const DECISION_MAKER_LABELS: Record<DecisionMakerStatus, string> = {
  UNKNOWN: 'Not known',
  IDENTIFIED: 'Identified',
  PRESENT: 'Present',
  ABSENT: 'Not present',
};

/** Every pipeline state, in the order a deal moves through them. */
export const PIPELINE_STATES = keysOf(PIPELINE_STATE_LABELS);

/** Every phase, in the order a deal moves through them. */
export const PIPELINE_PHASES = keysOf(PIPELINE_PHASE_LABELS);

/** The decision-maker answers as `{ value, label }`, for a select. */
export const DECISION_MAKER_OPTIONS = optionsOf(DECISION_MAKER_LABELS);

/**
 * The phase a state belongs to.
 *
 * A state the vocabulary does not know has no phase rather than a wrong one.
 *
 * @param state one of the fourteen pipeline states, or any code a record carries
 * @returns the phase a person sees it under
 */
export function phaseOf(state: OpportunityStatus): PipelinePhase;
export function phaseOf(state: string): PipelinePhase | undefined;
export function phaseOf(state: string): PipelinePhase | undefined {
  return Object.hasOwn(PHASE_OF_STATE, state)
    ? PHASE_OF_STATE[state as OpportunityStatus]
    : undefined;
}

export function pipelineStateLabel(state: string | null | undefined): string {
  return labelOf(PIPELINE_STATE_LABELS, state);
}

export function pipelinePhaseLabel(phase: string | null | undefined): string {
  return labelOf(PIPELINE_PHASE_LABELS, phase);
}

export function attentionReasonLabel(reason: string | null | undefined): string {
  return labelOf(ATTENTION_REASON_LABELS, reason);
}

export function decisionMakerLabel(status: string | null | undefined): string {
  return labelOf(DECISION_MAKER_LABELS, status);
}
